// src/modules/moduleLoader.js
//
// Loads and initializes modules based on frontend feature flags.
// Called from App.vue after authentication.

import { markRaw, defineAsyncComponent, shallowRef } from 'vue'
import { useFrontendFlagsStore } from '@/stores/frontendFlagsStore'
import * as modules from './index'

// Active module state
const activeModules = new Map() // moduleId → manifest
const activeStores = new Map() // storeId → store instance
const globalComponents = shallowRef([]) // [{ name, component }]

/**
 * Initialize all enabled modules.
 * Call after user login, when roles are known.
 *
 * @param {object} context - { roles: string[], userStore }
 */
export async function initializeModules(context) {
  const flagsStore = useFrontendFlagsStore()

  for (const [, manifest] of Object.entries(modules)) {
    if (!flagsStore.isModuleEnabled(manifest.id)) {
      console.log(`[moduleLoader] Module "${manifest.id}" is disabled, skipping`)
      continue
    }

    console.log(`[moduleLoader] Initializing module "${manifest.id}"`)
    activeModules.set(manifest.id, manifest)

    // Initialize stores
    for (const [storeKey, storeDef] of Object.entries(manifest.stores || {})) {
      try {
        const mod = await storeDef.import()
        // Find the store function by export key (useXStore pattern).
        // Cannot use v.name — minification mangles function names in production builds.
        const storeFunc = Object.entries(mod)
          .filter(([key]) => key.startsWith('use'))
          .map(([, val]) => val)
          .find(v => typeof v === 'function')
        if (storeFunc) {
          const store = storeFunc()
          activeStores.set(storeDef.storeId, store)
          if (storeDef.init) {
            storeDef.init(store, context)
          }
        }
      } catch (err) {
        console.error(
          `[moduleLoader] Failed to init store "${storeKey}" for module "${manifest.id}":`,
          err
        )
      }
    }

    // Register global components
    for (const [name, importFn] of Object.entries(manifest.globalComponents || {})) {
      globalComponents.value = [
        ...globalComponents.value,
        { name, component: markRaw(defineAsyncComponent(importFn)) },
      ]
    }
  }
}

/**
 * Clean up all active modules.
 * Call on user logout.
 */
export function cleanupModules() {
  for (const [, manifest] of activeModules) {
    for (const [storeKey, storeDef] of Object.entries(manifest.stores || {})) {
      const store = activeStores.get(storeDef.storeId)
      if (store && storeDef.cleanup) {
        try {
          storeDef.cleanup(store)
        } catch (err) {
          console.error(`[moduleLoader] Cleanup error for store "${storeKey}":`, err)
        }
      }
    }
  }
  activeModules.clear()
  activeStores.clear()
  globalComponents.value = []
}

/**
 * Get the list of route names that are currently active.
 * Used by the router to determine if a route should be accessible.
 */
export function getActiveRouteNames() {
  const names = new Set()
  for (const [, manifest] of activeModules) {
    for (const routeName of manifest.routes || []) {
      names.add(routeName)
    }
  }
  return names
}

/**
 * Get active navigation items grouped by section.
 * Used by HeaderComponent to build the menu.
 */
export function getActiveNavItems() {
  const items = []
  for (const [, manifest] of activeModules) {
    items.push(...(manifest.nav || []))
  }
  return items
}

/**
 * Check if a specific module is currently active.
 */
export function isModuleActive(moduleId) {
  return activeModules.has(moduleId)
}

export { globalComponents }
